/**
 * RecurrenceBlock — the task drawer's editable "Repeats" row (v3.x),
 * extracted from WorkItemDrawer (UX-11 PR-2). A recurring task shows its
 * cadence + Edit (→ updateRecurringSeries re-derives next_due + the live
 * due_date); a one-off task gets "+ Add a repeat" (→ attachRecurrenceToTask
 * turns it recurring in place). Both reuse the quick-add picker.
 * Stop/Delete live in the destructive block below it.
 */

import { useState, type ReactNode } from 'react';
import { Repeat } from 'lucide-react';
import type { WorkItemDetail } from '@/lib/api';
import type {
  useAttachRecurrence,
  useRecurringSeries,
  useUpdateRecurringSeries,
} from '@/lib/workspace';
import { TextAction } from '@/components/workspace/TextAction';
import {
  RecurrencePicker,
  draftFromSeries,
  recurrenceAnchors,
  recurrenceAnchorReady,
  EMPTY_RECURRENCE,
  type RecurrenceDraft,
} from '@/components/workspace/RecurrencePicker';

export interface RecurrenceBlockProps {
  detail: WorkItemDetail;
  canManage: boolean;
  ruleQuery: ReturnType<typeof useRecurringSeries>;
  updateSeries: ReturnType<typeof useUpdateRecurringSeries>;
  attachRecurrence: ReturnType<typeof useAttachRecurrence>;
}

export function RecurrenceBlock({
  detail,
  canManage,
  ruleQuery,
  updateSeries,
  attachRecurrence,
}: RecurrenceBlockProps): ReactNode {
  const [editingRepeat, setEditingRepeat] = useState(false);
  const [repeatDraft, setRepeatDraft] = useState<RecurrenceDraft>(EMPTY_RECURRENCE);

  return !editingRepeat ? (
    detail.recurrence_id ? (
      <span className="inline-flex items-center gap-1.5 align-middle">
        <Repeat size={13} className="text-warm-500" aria-hidden="true" />
        <span data-testid="drawer-repeat-summary">{detail.recurrence_summary ?? 'Recurring'}</span>
        {canManage && (
          <TextAction
            data-testid="drawer-repeat-edit"
            disabled={!ruleQuery.data}
            onClick={() => { if (ruleQuery.data) { setRepeatDraft(draftFromSeries(ruleQuery.data)); setEditingRepeat(true); } }}
            className="ml-1"
          >
            Edit
          </TextAction>
        )}
      </span>
    ) : (
      <span className="inline-flex items-center gap-1.5 align-middle">
        <span className="text-ink-soft" data-testid="drawer-repeat-summary">Does not repeat</span>
        {canManage && (
          <TextAction
            data-testid="drawer-repeat-add"
            onClick={() => { setRepeatDraft(EMPTY_RECURRENCE); setEditingRepeat(true); }}
            className="ml-1"
          >
            + Add a repeat
          </TextAction>
        )}
      </span>
    )
  ) : (
    <div className="flex flex-wrap items-center gap-2" data-testid="drawer-repeat-editor">
      <RecurrencePicker value={repeatDraft} onChange={setRepeatDraft} idPrefix="drawer" />
      <TextAction
        data-testid="drawer-repeat-save"
        disabled={repeatDraft.cadence === '' || !recurrenceAnchorReady(repeatDraft) || updateSeries.isPending || attachRecurrence.isPending}
        onClick={() => {
          if (repeatDraft.cadence === '') return;
          const schedule = { cadence: repeatDraft.cadence, ...recurrenceAnchors(repeatDraft) };
          if (detail.recurrence_id) {
            updateSeries.mutate({ id: detail.recurrence_id, edits: schedule }, { onSuccess: () => setEditingRepeat(false) });
          } else {
            attachRecurrence.mutate({ id: detail.id, input: schedule }, { onSuccess: () => setEditingRepeat(false) });
          }
        }}
      >
        Save
      </TextAction>
      <TextAction data-testid="drawer-repeat-cancel" onClick={() => setEditingRepeat(false)}>Cancel</TextAction>
    </div>
  );
}
